import { Controller } from "@hotwired/stimulus"
import { hideRailsuiModal, showRailsuiModal } from "utils/railsui_modal"

// Warn before leaving design-system Course Builder before the course is created.
export default class extends Controller {
  static targets = ["form", "submit", "modal"]
  static values = {
    message: {
      type: String,
      default: "If you leave now, your course will not be saved and your progress will be lost."
    },
    dirty: { type: Boolean, default: false },
    allowLeave: { type: Boolean, default: false }
  }

  connect() {
    this.pendingUrl = null
    this.boundBeforeUnload = this.beforeUnload.bind(this)
    this.boundBeforeVisit = this.beforeVisit.bind(this)
    this.boundDocumentClick = this.documentClick.bind(this)
    this.boundDenyLeave = this.denyLeave.bind(this)
    window.addEventListener("beforeunload", this.boundBeforeUnload)
    document.addEventListener("turbo:before-visit", this.boundBeforeVisit)
    document.addEventListener("click", this.boundDocumentClick, true)
    if (this.hasFormTarget) {
      // invalid does not bubble; capture so required-field failures clear the flag
      this.formTarget.addEventListener("invalid", this.boundDenyLeave, true)
    }
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.boundBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit)
    document.removeEventListener("click", this.boundDocumentClick, true)
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("invalid", this.boundDenyLeave, true)
    }
  }

  markDirty() {
    this.dirtyValue = true
  }

  allowLeave() {
    this.allowLeaveValue = true
  }

  denyLeave() {
    this.allowLeaveValue = false
  }

  documentClick(event) {
    if (!this.isConnectedGuard()) return
    if (!this.shouldWarn()) return
    if (event.defaultPrevented) return
    if (event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const anchor = event.target.closest?.("a[href]")
    if (!anchor) return
    if (this.element.contains(anchor)) return
    if (anchor.hasAttribute("download")) return
    if (anchor.getAttribute("target") === "_blank") return

    const href = anchor.getAttribute("href")
    if (!href || href.startsWith("#")) return

    let url
    try {
      url = new URL(anchor.href, window.location.href)
    } catch (_error) {
      return
    }

    if (
      url.origin === window.location.origin &&
      url.pathname === window.location.pathname &&
      url.search === window.location.search
    ) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    this.openLeaveModal(url.href)
  }

  beforeVisit(event) {
    if (!this.isConnectedGuard()) return
    if (!this.shouldWarn()) return

    event.preventDefault()
    this.openLeaveModal(event.detail.url)
  }

  beforeUnload(event) {
    if (!this.isConnectedGuard()) return
    if (!this.shouldWarn()) return
    event.preventDefault()
    event.returnValue = this.messageValue
    return this.messageValue
  }

  isConnectedGuard() {
    return Boolean(this.element?.isConnected)
  }

  async openLeaveModal(url) {
    this.pendingUrl = url
    if (!this.hasModalTarget) {
      this.confirmLeaveFallback()
      return
    }

    await showRailsuiModal(this.modalTarget)
  }

  confirmLeaveFallback() {
    if (window.confirm(this.messageValue)) {
      this.leaveWithoutSaving()
    } else {
      this.pendingUrl = null
    }
  }

  continueSetup(event) {
    event?.preventDefault()
    this.pendingUrl = null
    if (this.hasModalTarget) hideRailsuiModal(this.modalTarget)
  }

  leaveWithoutSaving(event) {
    event?.preventDefault()
    this.allowLeaveValue = true
    const url = this.pendingUrl
    this.pendingUrl = null
    if (this.hasModalTarget) hideRailsuiModal(this.modalTarget)

    if (!url) return

    if (window.Turbo?.visit) {
      window.Turbo.visit(url)
    } else {
      window.location.href = url
    }
  }

  shouldWarn() {
    return this.dirtyValue && !this.allowLeaveValue
  }
}
